/**
 * Canvas Compositor.
 *
 * Draws exactly one frame of the finished video for a given timeline
 * position. It is shared by the live preview and by the export renderer,
 * which guarantees the preview and the export match.
 *
 * Two source kinds:
 *   - procedural : pure function of time, so any position can be drawn
 *                  on demand (and can be drawn in reverse for Ping-Pong)
 *   - video      : an <video> element played in real time. Crossfades use
 *                  a pool of two elements, because a single element
 *                  cannot show two overlapping loops at once.
 */

import { crossfadeWeight } from './loop-engine.js';
import { resolveRenderer } from '../scenes/procedural/index.js';
import { createLogger } from '../utils/log.js';

const log = createLogger('compositor');

/** Cover-fit a source rect into a destination rect, cropping the overflow. */
export function computeCoverRect(sourceWidth, sourceHeight, destWidth, destHeight) {
  if (!sourceWidth || !sourceHeight) return { sx: 0, sy: 0, sw: destWidth, sh: destHeight };
  const sourceRatio = sourceWidth / sourceHeight;
  const destRatio = destWidth / destHeight;
  if (sourceRatio > destRatio) {
    // source is wider -> crop the sides
    const sw = sourceHeight * destRatio;
    return { sx: (sourceWidth - sw) / 2, sy: 0, sw, sh: sourceHeight };
  }
  const sh = sourceWidth / destRatio;
  return { sx: 0, sy: (sourceHeight - sh) / 2, sw: sourceWidth, sh };
}

export function createCompositor({
  scene,
  plan,
  width,
  height,
  allowFallback = false,
  fitMode = 'cover',
  reactiveStrength = 0.45,
}) {
  const { renderer, isFallback, reason } = resolveRenderer(scene, { allowFallback });
  const loopSeconds = Math.max(0.5, plan?.loopDuration ?? scene?.duration ?? 10);

  let videoPool = [];
  let offscreen = [];
  let ready = false;
  let started = false;
  let startTime = 0;

  const isVideoSource = Boolean(scene?.assetUrl) && scene?.assetType === 'video';
  const needsOffscreen = renderer && plan?.mode !== 'normal';

  function getOffscreen(index) {
    if (!offscreen[index]) {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      offscreen[index] = canvas;
    }
    return offscreen[index];
  }

  async function prepare() {
    if (isVideoSource) {
      const count = plan?.mode === 'crossfade' ? 2 : 1;
      videoPool = [];
      for (let i = 0; i < count; i += 1) {
        const el = document.createElement('video');
        el.src = scene.assetUrl;
        el.muted = true; // audio always comes from the Web Audio graph
        el.playsInline = true;
        el.preload = 'auto';
        el.loop = true;
        el.setAttribute('playsinline', '');
        el.setAttribute('webkit-playsinline', '');
        el.disablePictureInPicture = true;
        await waitForMetadata(el);
        videoPool.push(el);
      }
      log.info('video pool ready', { count, width: videoPool[0]?.videoWidth, height: videoPool[0]?.videoHeight });
    }
    ready = true;
    return api;
  }

  /** Start every real-time source. Called once, immediately before recording. */
  async function startAll() {
    startTime = performance.now() / 1000;
    started = true;
    for (const el of videoPool) {
      try {
        el.currentTime = 0;
        await el.play();
      } catch (error) {
        log.warn('video play() rejected', error);
      }
    }
  }

  function stopAll() {
    started = false;
    for (const el of videoPool) {
      try {
        el.pause();
      } catch {
        /* ignore */
      }
    }
  }

  /** Which video elements should be visible right now, and at what alpha. */
  function resolveVideoLayers(t) {
    const layers = [];
    if (!plan?.segments?.length) return layers;
    for (const segment of plan.segments) {
      if (t < segment.timelineStart - 1e-6 || t >= segment.timelineEnd + 1e-6) continue;
      const local = t - segment.timelineStart;
      let alpha = 1;
      if (plan.mode === 'crossfade') {
        if (segment.crossfadeIn > 0 && local < segment.crossfadeIn) {
          alpha = crossfadeWeight(local, segment.crossfadeIn);
        } else if (segment.crossfadeOut > 0 && local > segment.duration - segment.crossfadeOut) {
          alpha = 1 - crossfadeWeight(local - (segment.duration - segment.crossfadeOut), segment.crossfadeOut);
        }
      }
      layers.push({ segment, local, alpha, element: videoPool[segment.index % Math.max(1, videoPool.length)] });
    }
    return layers;
  }

  function drawFrame(ctx, t, features) {
    ctx.save();
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, width, height);

    if (isVideoSource) {
      const layers = resolveVideoLayers(t);
      if (!layers.length) {
        // Between segments (should not happen) - hold the last frame.
        const el = videoPool[0];
        if (el) drawVideoCover(ctx, el, 1);
      } else {
        for (const layer of layers) {
          if (layer.element && layer.element.readyState >= 2) {
            drawVideoCover(ctx, layer.element, layer.alpha);
          }
        }
      }
    } else if (renderer) {
      drawProcedural(ctx, t, features);
    } else {
      drawMissingAsset(ctx, scene, width, height);
    }

    ctx.restore();
  }

  function drawProcedural(ctx, t, features) {
    const audio = features ?? { level: 0, bass: 0, mid: 0, treble: 0, beat: 0 };
    if (!needsOffscreen) {
      // Normal / single-pass: draw straight onto the output canvas.
      const position = resolveProceduralTime(t);
      renderer.draw(ctx, {
        t: position.time,
        loopSeconds,
        w: width,
        h: height,
        audio,
        strength: reactiveStrength,
        quality: 'render',
      });
      return;
    }

    const layers = resolveProceduralLayers(t);
    for (const layer of layers) {
      const canvas = getOffscreen(layer.segment.index % 2);
      const octx = canvas.getContext('2d');
      octx.clearRect(0, 0, width, height);
      renderer.draw(octx, {
        t: layer.time,
        loopSeconds,
        w: width,
        h: height,
        audio,
        strength: reactiveStrength,
        quality: 'render',
      });
      ctx.globalAlpha = layer.alpha;
      ctx.drawImage(canvas, 0, 0, width, height);
      ctx.globalAlpha = 1;
    }
  }

  function resolveProceduralTime(t) {
    if (!plan?.segments?.length) return { time: t % loopSeconds };
    for (const segment of plan.segments) {
      if (t >= segment.timelineStart - 1e-6 && t < segment.timelineEnd + 1e-6) {
        const local = t - segment.timelineStart;
        return { time: segment.reversed ? Math.max(0, loopSeconds - local) : Math.min(loopSeconds, local) };
      }
    }
    return { time: t % loopSeconds };
  }

  function resolveProceduralLayers(t) {
    const layers = [];
    if (!plan?.segments?.length) {
      return [{ segment: { index: 0 }, time: t % loopSeconds, alpha: 1 }];
    }
    for (const segment of plan.segments) {
      if (t < segment.timelineStart - 1e-6 || t >= segment.timelineEnd + 1e-6) continue;
      const local = t - segment.timelineStart;
      let alpha = 1;
      if (segment.crossfadeIn > 0 && local < segment.crossfadeIn) {
        alpha = crossfadeWeight(local, segment.crossfadeIn);
      } else if (segment.crossfadeOut > 0 && local > segment.duration - segment.crossfadeOut) {
        alpha = 1 - crossfadeWeight(local - (segment.duration - segment.crossfadeOut), segment.crossfadeOut);
      }
      layers.push({
        segment,
        time: segment.reversed ? Math.max(0, loopSeconds - local) : Math.min(loopSeconds, local),
        alpha,
      });
    }
    return layers;
  }

  function drawVideoCover(ctx, element, alpha) {
    const sw = element.videoWidth;
    const sh = element.videoHeight;
    if (!sw || !sh) return;
    ctx.globalAlpha = Math.max(0, Math.min(1, alpha));
    if (fitMode === 'contain-blur') {
      // Blurred fill behind a letterboxed clip.
      const { sx, sy, sw: cw, sh: ch } = computeCoverRect(sw, sh, width, height);
      ctx.drawImage(element, sx, sy, cw, ch, 0, 0, width, height);
      const scale = Math.min(width / sw, height / sh);
      const dw = sw * scale;
      const dh = sh * scale;
      ctx.globalAlpha = Math.max(0, Math.min(1, alpha)) * 0.35;
      ctx.drawImage(element, (width - dw) / 2, (height - dh) / 2, dw, dh);
    } else {
      const { sx, sy, sw: cw, sh: ch } = computeCoverRect(sw, sh, width, height);
      ctx.drawImage(element, sx, sy, cw, ch, 0, 0, width, height);
    }
    ctx.globalAlpha = 1;
  }

  const api = {
    prepare,
    startAll,
    stopAll,
    drawFrame,
    get renderer() {
      return renderer;
    },
    get isFallback() {
      return isFallback;
    },
    get sourceKind() {
      return isVideoSource ? 'video' : renderer ? 'procedural' : 'none';
    },
    get reason() {
      return reason;
    },
    get isReady() {
      return ready;
    },
    /** Current elapsed wall-clock seconds since startAll(). */
    get elapsed() {
      return started ? performance.now() / 1000 - startTime : 0;
    },
    diagnostics() {
      return {
        sourceKind: api.sourceKind,
        rendererId: renderer?.id ?? null,
        isFallback,
        reason,
        loopSeconds,
        mode: plan?.mode ?? null,
        segments: plan?.segments?.length ?? 0,
        videoElements: videoPool.length,
        videoResolution: videoPool[0] ? `${videoPool[0].videoWidth}x${videoPool[0].videoHeight}` : null,
      };
    },
    dispose() {
      stopAll();
      for (const el of videoPool) {
        try {
          el.removeAttribute('src');
          el.load();
        } catch {
          /* ignore */
        }
      }
      videoPool = [];
      offscreen = [];
    },
  };

  return api;
}

function waitForMetadata(video, timeoutMs = 20000) {
  return new Promise((resolve, reject) => {
    if (video.readyState >= 1 && video.videoWidth) {
      resolve();
      return;
    }
    const timer = setTimeout(() => {
      cleanup();
      reject(new Error('Timed out while loading the scene video.'));
    }, timeoutMs);
    const onLoaded = () => {
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      reject(new Error(`The scene video could not be decoded (${video.error?.message ?? 'unknown error'}).`));
    };
    function cleanup() {
      clearTimeout(timer);
      video.removeEventListener('loadedmetadata', onLoaded);
      video.removeEventListener('error', onError);
    }
    video.addEventListener('loadedmetadata', onLoaded);
    video.addEventListener('error', onError);
  });
}

/** Shown when a scene has neither an asset nor a renderer and the user
 *  explicitly asked to render anyway. Never pretends to be a real scene. */
export function drawMissingAsset(ctx, scene, width, height) {
  const gradient = ctx.createLinearGradient(0, 0, width, height);
  gradient.addColorStop(0, '#14161C');
  gradient.addColorStop(1, '#0A0B0F');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);

  ctx.strokeStyle = 'rgba(255,122,0,0.55)';
  ctx.lineWidth = Math.max(2, width * 0.004);
  ctx.strokeRect(width * 0.08, height * 0.42, width * 0.84, height * 0.16);

  ctx.fillStyle = 'rgba(255,255,255,0.82)';
  ctx.font = `600 ${Math.round(width * 0.045)}px system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillText(scene?.name ?? 'Scene', width / 2, height * 0.475);

  ctx.fillStyle = 'rgba(255,255,255,0.5)';
  ctx.font = `400 ${Math.round(width * 0.03)}px system-ui, sans-serif`;
  ctx.fillText('No asset for this scene yet', width / 2, height * 0.515);
  ctx.fillText('Generate a loop, then import it', width / 2, height * 0.548);
}
